import { useQueryClient } from "@tanstack/react-query"
import { useEffect, createContext, useContext, useMemo } from "react"
import Loader from "../../components/loader"
import { useCurrentUser } from "./hooks"
import { AuthChannel, IUser } from "./types"
interface AuthContextProps {
  user?: IUser
  isLoading: boolean
  canAccessActiveProfile: boolean
}

interface Props {
  children: React.ReactNode
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined)

const AuthProvider: React.FC<Props> = ({ children }) => {
  const queryClient = useQueryClient()

  const {
    data: user,
    isInitialLoading: isLoading,
    enable,
    disable,
  } = useCurrentUser()
  const canAccessActiveProfile = useMemo(() => {
    if (!user) return true
    return (
      !!user?.profiles?.includes("member") &&
      !!user?.profiles?.includes("respondent")
    )
  }, [user])

  const value = useMemo<AuthContextProps>(
    () => ({ user, isLoading, canAccessActiveProfile }),
    [user, isLoading, canAccessActiveProfile]
  )

  useEffect(() => {
    const authChannel = new BroadcastChannel(AuthChannel.Name)
    authChannel.onmessage = (event) => {
      if (event.data === AuthChannel.LoginEvent) {
        enable()
        queryClient.resetQueries([])
      }
      if (event.data === AuthChannel.LogoutEvent) {
        disable()
        queryClient.removeQueries()
      }
    }
    return () => authChannel.close()
  }, [queryClient, enable, disable])

  if (isLoading) return <Loader />

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const auth = useContext(AuthContext)
  if (auth === undefined) {
    throw new Error(`useAuth must be used within a AuthProvider`)
  }

  return auth
}

export * from "./utils"
export { useLogin, useLogout } from "./hooks"

export default AuthProvider
